'use client';

/**
 * NumberRenderer Component
 *
 * Renders NUMBER, CURRENCY, PERCENTAGE field types.
 * Supports display, edit, and inline modes.
 */

import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { getNeonStyles, type NeonAccent } from '../../../theme/palette';
import { transitionPresets } from '../../../theme/transitions';
import type { FieldDefinition, FieldMode } from '../../../types/entityFramework';
import { formatCurrency, formatNumber, formatPercentage } from '../../../utils/formatters';
import { transientOptions } from '../../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface NumberRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value */
  value: unknown;
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTextFieldProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $hasError: boolean;
  $size: 'small' | 'medium' | 'large';
}

const StyledTextField = styled(
  TextField,
  transientOptions,
)<StyledTextFieldProps>(({ theme, $isDark, $neonAccent, $hasError, $size }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const sizeStyles = {
    small: { fontSize: '0.875rem', padding: '8px 12px' },
    medium: { fontSize: '1rem', padding: '12px 16px' },
    large: { fontSize: '1.125rem', padding: '16px 20px' },
  };

  const s = sizeStyles[$size];

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      transition: transitionPresets.input,

      '& input': {
        fontSize: s.fontSize,
        padding: s.padding,
        textAlign: 'right',
      },

      '& fieldset': {
        borderColor: $hasError
          ? theme.palette.error.main
          : neonStyles
            ? neonStyles.border
            : $isDark
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(0, 0, 0, 0.15)',
        transition: transitionPresets.input,
      },

      '&:hover fieldset': {
        borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',

        '& fieldset': {
          borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 2,
          boxShadow: neonStyles ? `0 0 12px ${neonStyles.glow}` : undefined,
        },
      },

      '&.Mui-disabled': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)',
      },
    },

    '& .MuiInputLabel-root': {
      color: $isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      fontSize: '0.875rem',

      '&.MuiInputLabel-shrink': {
        backgroundColor: $isDark ? '#1a1a1a' : '#ffffff',
        padding: '0 6px',
        marginLeft: '-4px',
      },

      '&.Mui-focused': {
        color: neonStyles ? neonStyles.base : theme.palette.primary.main,
      },
    },

    '& .MuiFormHelperText-root': {
      marginLeft: 4,
      marginTop: 6,
    },
  };
});

const DisplayValue = styled(
  Typography,
  transientOptions,
)<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
  fontVariantNumeric: 'tabular-nums',
}));

const InlineInput = styled(
  TextField,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: 'transparent',
      transition: transitionPresets.input,

      '& fieldset': {
        borderColor: 'transparent',
      },

      '&:hover fieldset': {
        borderColor: neonStyles
          ? alpha(neonStyles.border, 0.5)
          : $isDark
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        '& fieldset': {
          borderColor: neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 1,
        },
      },
    },

    '& input': {
      padding: '4px 8px',
      textAlign: 'right',
    },
  };
});

// ============================================================================
// Helpers
// ============================================================================

const getCurrencySymbol = (currencyCode: string = 'USD'): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '\u20AC',
    GBP: '\u00A3',
    JPY: '\u00A5',
    CAD: 'C$',
    AUD: 'A$',
  };
  return symbols[currencyCode] || currencyCode;
};

// ============================================================================
// Component
// ============================================================================

export const NumberRenderer: React.FC<NumberRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled = false,
  size = 'medium',
  neonAccent,
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Parse numeric value
  const numericValue = value != null ? Number(value) : null;
  const isValidNumber = numericValue !== null && !Number.isNaN(numericValue);

  // Format display value based on field type
  const getDisplayValue = (): string => {
    if (!isValidNumber) return '-';

    switch (field.fieldType) {
      case 'CURRENCY':
        return formatCurrency(numericValue, field.currency || 'USD');
      case 'PERCENTAGE':
        return formatPercentage(numericValue, field.precision ?? 2);
      default:
        return formatNumber(numericValue, {
          minimumFractionDigits: field.precision ?? 0,
          maximumFractionDigits: field.precision ?? 2,
        });
    }
  };

  // Handle value change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === '' || inputValue === '-') {
      onChange?.(null);
      return;
    }

    const parsed = parseFloat(inputValue);
    if (!Number.isNaN(parsed)) {
      onChange?.(parsed);
    }
  };

  // Get input adornments
  const getStartAdornment = () => {
    if (field.fieldType === 'CURRENCY') {
      return <InputAdornment position="start">{getCurrencySymbol(field.currency)}</InputAdornment>;
    }
    return undefined;
  };

  const getEndAdornment = () => {
    if (field.fieldType === 'PERCENTAGE') {
      return <InputAdornment position="end">%</InputAdornment>;
    }
    return undefined;
  };

  // Display mode
  if (mode === 'display') {
    return (
      <Box className={className}>
        <DisplayValue variant="body1" $isDark={isDark}>
          {getDisplayValue()}
        </DisplayValue>
      </Box>
    );
  }

  // Inline mode
  if (mode === 'inline') {
    return (
      <InlineInput
        value={isValidNumber ? numericValue : ''}
        onChange={handleChange}
        type="number"
        placeholder={field.placeholder}
        disabled={disabled || field.disabled}
        error={!!error}
        size="small"
        fullWidth
        $isDark={isDark}
        $neonAccent={neonAccent}
        className={className}
        slotProps={{
          htmlInput: {
            min: field.min,
            max: field.max,
            step: field.precision ? 10 ** -field.precision : 1,
          },
          input: {
            startAdornment: getStartAdornment(),
            endAdornment: getEndAdornment(),
          },
        }}
      />
    );
  }

  // Edit mode
  return (
    <StyledTextField
      value={isValidNumber ? numericValue : ''}
      onChange={handleChange}
      type="number"
      label={field.label}
      placeholder={field.placeholder}
      helperText={error || field.helpText}
      error={!!error}
      disabled={disabled || field.disabled}
      required={field.required}
      fullWidth
      variant="outlined"
      $isDark={isDark}
      $neonAccent={neonAccent}
      $hasError={!!error}
      $size={size}
      className={className}
      slotProps={{
        htmlInput: {
          min: field.min,
          max: field.max,
          step: field.precision ? 10 ** -field.precision : 1,
        },
        input: {
          startAdornment: getStartAdornment(),
          endAdornment: getEndAdornment(),
        },
      }}
    />
  );
};

NumberRenderer.displayName = 'NumberRenderer';

export default NumberRenderer;
